import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { User } from '../types';
import { usePageTitle } from "../hooks/usePageTitle";
import GoogleSheetsApiService from '../services/googleSheetsApi';

interface WagerProps {
  user: User;
}

interface LeaderboardEntry {
  rank: number;
  user: string;
  balance: number;
  totalMined: number;
  distance: number;
  lastMined: string;
  votingShare: number;
}

export default function Wager({ user }: WagerProps) {
  usePageTitle("The DAISY™ Degenerate Dashboard");
  
  // Separate state for each bet
  const [mareAffairBet, setMareAffairBet] = useState<'rachoon' | 'kat' | null>(null);
  const [patrickBet, setPatrickBet] = useState<'patrick-over' | 'patrick-under' | null>(null);
  const [milkBet, setMilkBet] = useState<'milk-over' | 'milk-under' | null>(null);
  const [melathonBet, setMelathonBet] = useState<'melathon-over' | 'melathon-under' | null>(null);
  const [trimbawolfBet, setTrimbawolfBet] = useState<'trimbawolf-over' | 'trimbawolf-under' | null>(null);
  
  const [mareAffairAmount, setMareAffairAmount] = useState<number>(0);
  const [patrickAmount, setPatrickAmount] = useState<number>(0);
  const [milkAmount, setMilkAmount] = useState<number>(0);
  const [melathonAmount, setMelathonAmount] = useState<number>(0);
  const [trimbawolfAmount, setTrimbawolfAmount] = useState<number>(0);
  
  const [mareAffairUser, setMareAffairUser] = useState<string>('');
  const [patrickUser, setPatrickUser] = useState<string>('');
  const [milkUser, setMilkUser] = useState<string>('');
  const [melathonUser, setMelathonUser] = useState<string>('');
  const [trimbawolfUser, setTrimbawolfUser] = useState<string>('');
  
  const [betSlip, setBetSlip] = useState<string | null>(null);
  const [showBetSlipPopup, setShowBetSlipPopup] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDecimalPopup, setShowDecimalPopup] = useState(false);
  const [showMaxWagerPopup, setShowMaxWagerPopup] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [activeBet, setActiveBet] = useState<'mare-affair' | 'patrick' | 'milk' | 'melathon' | 'trimbawolf' | 'generic' | null>(null);
  const [showExplainer, setShowExplainer] = useState(false);

  // OBWI Wager Center (generic slip generator)
  const [genWagerer, setGenWagerer] = useState<string>(user.username);
  const [genWagererOther, setGenWagererOther] = useState<string>('');
  const [genMiner, setGenMiner] = useState<string>('Old Riley');
  const [genMinerOther, setGenMinerOther] = useState<string>('');
  const [genAmount, setGenAmount] = useState<number | ''>(3);
  const [genError, setGenError] = useState<string | null>(null);
  const [genSlip, setGenSlip] = useState<{ wagerer: string, miner: string, amount: number, side?: 'over' | 'under', pwp?: string, heatSize?: number, variant?: 'pwp' | 'closest' } | null>(null);
  const [genOverUnder, setGenOverUnder] = useState<'over' | 'under' | ''>('over');
  const [genPwp, setGenPwp] = useState<string>('');
  // Second generator states (right column)
  const [gen2Wagerer, setGen2Wagerer] = useState<string>('');
  const [gen2WagererOther, setGen2WagererOther] = useState<string>('');
  const [gen2Miner, setGen2Miner] = useState<string>('');
  const [gen2MinerOther, setGen2MinerOther] = useState<string>('');
  const [gen2Amount, setGen2Amount] = useState<number | ''>('');
  const [gen2Error, setGen2Error] = useState<string | null>(null);
  const [gen2OverUnder, setGen2OverUnder] = useState<'over' | 'under' | ''>('');
  const [gen2Pwp, setGen2Pwp] = useState<string>('');
  const [gen2HeatSize, setGen2HeatSize] = useState<number | ''>('');

  // Custom bet + PWP calculator
  const [customBetText, setCustomBetText] = useState<string>('');
  const [pwpCalcTime, setPwpCalcTime] = useState<string>('');
  const [pwpCalcEffort, setPwpCalcEffort] = useState<'all_out' | 'pretty_hard' | 'hustle' | 'super_chill' | ''>('');
  const [pwpCalcResult, setPwpCalcResult] = useState<string>('');
  const [showPwpCalc, setShowPwpCalc] = useState<boolean>(false);
  const [showObwiIndex, setShowObwiIndex] = useState<boolean>(false);

  const effortToMultiplier = (effort: typeof pwpCalcEffort): number => {
    switch (effort) {
      case 'all_out':
        return 1.25;
      case 'pretty_hard':
        return 1.15;
      case 'hustle':
        return 1.10;
      case 'super_chill':
        return 0.85;
      default:
        return 1;
    }
  };

  const parseTimeToSeconds = (input: string): number | null => {
    const trimmed = input.trim();
    if (!trimmed) return null;
    const parts = trimmed.split(':').map((p) => p.trim());
    if (parts.length !== 2) return null; // mm:ss only
    if (parts.some((p) => p === '' || isNaN(Number(p)))) return null;
    const [m, s] = parts.map(Number);
    if (s >= 60 || m < 0 || s < 0) return null;
    return m * 60 + s;
  };

  const formatSecondsToHms = (totalSeconds: number): string => {
    const sign = totalSeconds < 0 ? '-' : '';
    const sec = Math.max(0, Math.round(Math.abs(totalSeconds)));
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    const two = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    return `${sign}${m}:${two(s)}`;
  };

  useEffect(() => {
    const base = parseTimeToSeconds(pwpCalcTime);
    if (base == null || !pwpCalcEffort) {
      setPwpCalcResult('');
      return;
    }
    const resultSec = Math.round(base * effortToMultiplier(pwpCalcEffort));
    setPwpCalcResult(formatSecondsToHms(resultSec));
  }, [pwpCalcTime, pwpCalcEffort]);

  // OBWI Index calculator
  const [obwiActual, setObwiActual] = useState<string>('');
  const [obwiPwp, setObwiPwp] = useState<string>('');
  const [obwiScore, setObwiScore] = useState<string>('');

  useEffect(() => {
    const actual = parseTimeToSeconds(obwiActual);
    const pwp = parseTimeToSeconds(obwiPwp);
    if (actual == null || pwp == null || pwp <= 0) {
      setObwiScore('');
      return;
    }
    const diff = Math.abs(actual - pwp);
    setObwiScore((diff / pwp).toFixed(4));
  }, [obwiActual, obwiPwp]);

  // Fetch leaderboard data to get usernames
  useEffect(() => {
    const fetchLeaderboardData = async () => {
      try {
        const sheetsService = GoogleSheetsApiService.getInstance();
        const leaderboardData = await sheetsService.getLeaderboardData();
        setLeaderboardData(leaderboardData);
      } catch (err) {
        console.error('Failed to fetch leaderboard data:', err);
      }
    };

    fetchLeaderboardData();
  }, []);

  const handleBetAmountChange = (e: React.ChangeEvent<HTMLInputElement>, setAmount: (value: number) => void) => {
    const value = e.target.value;
    if (value.includes('.')) {
      setShowDecimalPopup(true);
      e.target.value = value.replace('.', ''); // Remove the decimal point
      setTimeout(() => setShowDecimalPopup(false), 2000); // Hide popup after 2 seconds
      return;
    }
    
    const numValue = Number(value) || 0;
    if (numValue > 8) {
      setShowMaxWagerPopup(true);
      e.target.value = '8'; // Set to max value
      setTimeout(() => setShowMaxWagerPopup(false), 2000); // Hide popup after 2 seconds
      setAmount(8);
      return;
    }
    
    setAmount(numValue);
  };

  // Generator amount change (Max 8, no decimals)
  const handleGenAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.includes('.')) {
      setGenError("DAISY™ don't do decimals");
      e.target.value = value.replace('.', '');
      return;
    }
    const num = Number(value);
    if (!value) {
      setGenAmount('');
      setGenError(null);
      return;
    }
    if (isNaN(num) || num <= 0) {
      setGenAmount('');
      setGenError('Enter a whole number 1–8');
      return;
    }
    if (num > 8) {
      setGenAmount(8);
      setGenError('Max wager is 8');
      e.target.value = '8';
      return;
    }
    setGenError(null);
    setGenAmount(num);
  };

  const handleGen2AmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.includes('.')) {
      setGen2Error("DAISY™ don't do decimals");
      e.target.value = value.replace('.', '');
      return;
    }
    const num = Number(value);
    if (!value) {
      setGen2Amount('');
      setGen2Error(null);
      return;
    }
    if (isNaN(num) || num <= 0) {
      setGen2Amount('');
      setGen2Error('Enter a whole number 1–8');
      return;
    }
    if (num > 8) {
      setGen2Amount(8);
      setGen2Error('Max wager is 8');
      e.target.value = '8';
      return;
    }
    setGen2Error(null);
    setGen2Amount(num);
  };

  const handleGenerateSlip = (e: React.FormEvent) => {
    e.preventDefault();
    const w = (genWagerer === 'other' ? genWagererOther.trim() : genWagerer).trim();
    const m = (genMiner === 'other' ? genMinerOther.trim() : genMiner).trim();
    const a = typeof genAmount === 'number' ? genAmount : 0;
    if (!w) { setGenError('Select or enter a Wagerer'); return; }
    if (!m) { setGenError('Select or enter a Miner'); return; }
    if (!genOverUnder) { setGenError('Choose Over or Under'); return; }
    if (!a || a < 1 || a > 8) { setGenError('Enter a whole number 1–8'); return; }
    const wNorm = w.toLowerCase();
    const mNorm = m.toLowerCase();

    // Can't bet on (or against) yourself (except when your username is one of the team picks).
    if (wNorm === mNorm && wNorm !== 'old riley' && wNorm !== 'billy') {
      setGenError("Can't bet on (or against) yourself");
      return;
    }

    // Can't bet against your own team.
    const isOldRiley = wNorm === 'old riley';
    const isBilly = wNorm === 'billy';
    const isPickingOldRiley = mNorm === 'old riley';
    const isPickingBilly = mNorm === 'billy';
    if (
      (isOldRiley && isPickingBilly) ||
      (isBilly && isPickingOldRiley)
    ) {
      setGenError("You can't bet against your own team");
      return;
    }
    setGenError(null);
    setGenSlip({ wagerer: w, miner: m, amount: a, side: genOverUnder, pwp: genPwp.trim() || undefined, variant: 'pwp' });
    // show popup using existing system
    setActiveBet('mare-affair'); // temporary to satisfy non-null; will override below
    setActiveBet('generic' as any);
    setBetSlip('generated');
    setShowBetSlipPopup(true);
    // keep inputs as-is so the form does not auto-clear
  };

  const handleGenerateSlip2 = (e: React.FormEvent) => {
    e.preventDefault();
    const w = (gen2Wagerer === 'other' ? gen2WagererOther.trim() : gen2Wagerer).trim();
    const m = (gen2Miner === 'other' ? gen2MinerOther.trim() : gen2Miner).trim();
    const a = typeof gen2Amount === 'number' ? gen2Amount : 0;
    const h = typeof gen2HeatSize === 'number' ? gen2HeatSize : 0;
    if (!w) { setGen2Error('Select or enter a Wagerer'); return; }
    if (!m) { setGen2Error('Select or enter a Miner'); return; }
    if (!h || h < 2) { setGen2Error('Select heat size (≥ 2)'); return; }
    if (!a || a < 1 || a > 7) { setGen2Error('Enter a whole number 1–7'); return; }
    setGen2Error(null);
    setGenSlip({ wagerer: w, miner: m, amount: a, heatSize: h, pwp: gen2Pwp.trim() || undefined, variant: 'closest' });
    setActiveBet('generic');
    setBetSlip('generated');
    setShowBetSlipPopup(true);
  };


  const handleSubmit = (e: React.FormEvent, amount: number, user: string, betType: 'mare-affair' | 'patrick' | 'milk' | 'melathon' | 'trimbawolf') => {
    e.preventDefault();
    if (amount <= 0) {
      setError('Please enter a valid bet amount');
      return;
    }
    if (!user) {
      setError('Please select a user');
      return;
    }
    setError(null);
    setActiveBet(betType);
    setBetSlip('generated');
    setShowBetSlipPopup(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            The <span className="font-bold">D</span><span className="text-[#00CED1]">AI</span><span className="font-bold">SY</span><span className="text-xl align-top ml-0.5">™</span> Degenerate Dashboard
          </h1>
          <button
            onClick={() => setShowExplainer(!showExplainer)}
            className="text-xs text-wingo-600 hover:text-wingo-500 italic -mt-1 mb-2 block text-center mx-auto"
          >
            So how do I bet on things?
          </button>
        </div>

        {/* Tools toggles (PWP hidden for now) */}
        <div className="max-w-4xl mx-auto mb-3 hidden">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setShowPwpCalc(true)}
                className="px-3 py-1 text-xs sm:text-sm rounded-md border border-gray-300 text-gray-800 bg-white hover:bg-gray-50"
              >
                What's my PWP?
              </button>
            </div>
          </div>

        {/* OBWI Wager Center: Single generator (Old Riley vs Billy) */}
        <div className="max-w-4xl mx-auto mb-8 grid grid-cols-1 gap-4">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-6 max-w-sm w-full mx-auto md:max-w-none">
            <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">3/19 Day: Old Riley vs. Billy</h2>
            <form onSubmit={handleGenerateSlip}>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Team <span className="text-gray-400">(who you are wagering on)</span></label>
                  <select
                    value={genMiner}
                    onChange={(e) => setGenMiner(e.target.value)}
                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-wingo-500 focus:border-wingo-500"
                  >
                    <option value="">Select team</option>
                    <option value="Old Riley" disabled={genWagerer.toLowerCase() === 'billy'}>Old Riley</option>
                    <option value="Billy" disabled={genWagerer.toLowerCase() === 'old riley'}>Billy</option>
                  </select>
                </div>
                <div className="hidden">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Miner's Target PWP</label>
                  <input
                    type="text"
                    placeholder="e.g. 8:57"
                    value={genPwp}
                    onChange={(e) => setGenPwp(e.target.value)}
                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-wingo-500 focus:border-wingo-500"
                  />
                </div>
                <div className="hidden">
                  <label className="block text-xs font-medium text-gray-700 mb-1">My Pick</label>
                  <div className="flex gap-3">
                    <label className="inline-flex items-center gap-1 text-sm text-gray-700">
                      <input type="radio" name="gen-ou" checked={genOverUnder === 'over'} onChange={() => setGenOverUnder('over')} className="text-wingo-600 focus:ring-wingo-500" />
                      Over PWP
                    </label>
                    <label className="inline-flex items-center gap-1 text-sm text-gray-700">
                      <input type="radio" name="gen-ou" checked={genOverUnder === 'under'} onChange={() => setGenOverUnder('under')} className="text-wingo-600 focus:ring-wingo-500" />
                      Under PWP
                    </label>
                  </div>
                </div>
              </div>
              {/* Bottom row like Brooklyn Mile cards */}
              <div className="flex items-end gap-3 mt-4">
                <div className="flex-1 sm:flex-1 md:w-32">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Wagerer <span className="text-gray-400">(that's you)</span>:</label>
                  <select
                    value={genWagerer}
                    onChange={(e) => setGenWagerer(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-wingo-500 focus:border-wingo-500"
                    style={{ height: '28px' }}
                  >
                    <option value="">Select user</option>
                    {leaderboardData.map((entry) => (
                      <option key={`w-${entry.user}`} value={entry.user}>
                        {entry.user}
                      </option>
                    ))}
                    <option value="Guest">Guest</option>
                    <option value="other">Other</option>
                  </select>
                  {genWagerer === 'other' && (
                    <input
                      type="text"
                      placeholder="Enter name"
                      value={genWagererOther}
                      onChange={(e) => setGenWagererOther(e.target.value)}
                      className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-wingo-500 focus:border-wingo-500"
                      style={{ height: '28px' }}
                    />
                  )}
                </div>
                <div className="w-20">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Amount:</label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={genAmount === '' ? '' : genAmount}
                      onChange={handleGenAmountChange}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-wingo-500 focus:border-wingo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="0"
                      style={{ fontSize: '16px', height: '28px' }}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#E6C200] font-bold text-xs">W</span>
                  </div>
                </div>
              <div className="flex-shrink-0">
                  <button
                    type="submit"
                    className="bg-wingo-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-wingo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wingo-500"
                    style={{ height: '28px' }}
                  >
                    Wager
                  </button>
                </div>
              </div>
              {genError && <p className="text-xs text-red-600 mt-2">{genError}</p>}
            </form>

            <p className="text-[9px] text-gray-500 mt-2 text-center mb-0">
              Wagers subject to D<span className="text-cyan-400">AI</span>SY™ approval and may be voided at any time for any reason. <span className="inline sm:block">Max Wager: <span className="text-[#E6C200] font-bold">W</span> 8</span>
            </p>
          </div>
          {/* Second column generator */}
          <div className="hidden bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-6 max-w-sm w-full mx-auto md:max-w-none">
            <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">Closest to the Penta</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleGenerateSlip2(e); }}>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Miner <span className="text-gray-400">(who you are wagering on)</span></label>
                  <select
                    value={gen2Miner}
                    onChange={(e) => setGen2Miner(e.target.value)}
                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-wingo-500 focus:border-wingo-500"
                  >
                    <option value="">Select miner</option>
                    {leaderboardData.map((entry) => (
                      <option key={`m2-${entry.user}`} value={entry.user}>{entry.user}</option>
                    ))}
                    <option value="Guest">Guest</option>
                    <option value="other">Other</option>
                  </select>
                  {gen2Miner === 'other' && (
                    <input
                      type="text"
                      placeholder="Enter name"
                      value={gen2MinerOther}
                      onChange={(e) => setGen2MinerOther(e.target.value)}
                      className="mt-2 w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-wingo-500 focus:border-wingo-500"
                    />
                  )}
                </div>
                {/* Optional PWP input */}
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Heat Size</label>
                    <select
                      value={gen2HeatSize === '' ? '' : String(gen2HeatSize)}
                      onChange={(e) => setGen2HeatSize(e.target.value ? Number(e.target.value) : '')}
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-wingo-500 focus:border-wingo-500"
                    >
                      <option value="">Select heat size</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="6">6</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Miner's Target PWP (optional)</label>
                    <input
                      type="text"
                      placeholder="e.g. 8:57"
                      value={gen2Pwp}
                      onChange={(e) => setGen2Pwp(e.target.value)}
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-wingo-500 focus:border-wingo-500"
                    />
                  </div>
                </div>
                <div className="flex items-end gap-3 mt-4">
                <div className="flex-1 sm:flex-1 md:w-32">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Wagerer <span className="text-gray-400">(that's you)</span>:</label>
                  <select
                    value={gen2Wagerer}
                    onChange={(e) => setGen2Wagerer(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-wingo-500 focus:border-wingo-500"
                    style={{ height: '28px' }}
                  >
                    <option value="">Select user</option>
                    {leaderboardData.map((entry) => (
                      <option key={`w2-${entry.user}`} value={entry.user}>{entry.user}</option>
                    ))}
                    <option value="Guest">Guest</option>
                    <option value="other">Other</option>
                  </select>
                  {gen2Wagerer === 'other' && (
                    <input
                      type="text"
                      placeholder="Enter name"
                      value={gen2WagererOther}
                      onChange={(e) => setGen2WagererOther(e.target.value)}
                      className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-wingo-500 focus:border-wingo-500"
                      style={{ height: '28px' }}
                    />
                  )}
                </div>
                <div className="w-20">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Amount:</label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={gen2Amount === '' ? '' : gen2Amount}
                      onChange={handleGen2AmountChange}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-wingo-500 focus:border-wingo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="0"
                      style={{ fontSize: '16px', height: '28px' }}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#E6C200] font-bold text-xs">W</span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <button type="submit" className="bg-wingo-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-wingo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wingo-500" style={{ height: '28px' }}>
                    Wager
                  </button>
                </div>
              </div>
            </div>
              {gen2Error && <p className="text-xs text-red-600 mt-2">{gen2Error}</p>}
            </form>
            <p className="text-[9px] text-gray-500 mt-2 text-center mb-0">
              Wagers subject to D<span className="text-cyan-400">AI</span>SY™ approval and may be voided at any time for any reason. <span className="inline sm:block">Max Wager: <span className="text-[#E6C200] font-bold">W</span> 8</span>
            </p>
            <p className="text-[10px] text-gray-600 mt-2 text-center">
              Note: Winners determined based on OBWI Index, a proprietary proportional PentaWingoPoint permutation. Lowest score wins.{' '}
              <button type="button" onClick={() => setShowObwiIndex(true)} className="text-wingo-600 underline hover:text-wingo-700">Calculate OBWI Index here</button>.
            </p>
          </div>
        </div>

        {/* Submit custom bet to Coach DAISY */}
        <div className="max-w-2xl mx-auto mb-10 bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-6">
          <p className="text-sm text-gray-900 text-center">
            Send wager proposal to Coach DAISY for approval/rejection at{' '}
            <a href="sms:9299254744" className="text-wingo-600 underline hover:text-wingo-700">929-WAK-GRIG</a>.
          </p>
          <p className="text-[10px] italic text-gray-500 mt-1 text-center">
            E.g.: W 3 that Old Riley wins 3/19 day
          </p>
        </div>

        {/* 3/19 Conversions */}
        <div className="max-w-2xl mx-auto mb-10 bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
            Need conversions for 3/19 Day?
          </h3>
          <p className="text-sm text-gray-600 text-center mb-4">
            Use the DAISY™ converter on the <a href="/319" className="text-wingo-600 underline hover:text-wingo-700 font-semibold">319 page</a>.
          </p>
          <div className="flex justify-center">
            <a
              href="/319"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-wingo-600 text-white text-sm font-medium hover:bg-wingo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wingo-500"
            >
              Open 3/19 Conversions
            </a>
          </div>
        </div>

        {/* Sponsored by Old Balance */}
        <div className="max-w-2xl mx-auto mb-10 bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-6">
          <a href="/old-balance" className="block group">
            <img
              src="https://wingo320.com/old_balance.png"
              alt="Sponsored by Old Balance"
              className="mx-auto w-full max-w-sm h-auto object-contain transition-transform duration-200 group-hover:scale-[1.01]"
            />
            <p className="text-sm text-gray-700 mt-3 text-center leading-snug">
              <span className="font-semibold text-gray-900">3/19 Day</span> is Sponsored by{' '}
              <span className="font-semibold text-wingo-600">Old Balance</span>: <span className="italic">Lace yourself. Before you face yourself.</span>
            </p>
          </a>
        </div>

        {/* Explainer Popup */}
        {showExplainer && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-4">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900">How to Bet on Things</h2>
                <button
                  onClick={() => setShowExplainer(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <span className="font-semibold">+110:</span> For every <span className="text-[#E6C200] font-bold">W </span>100 bet, you can win <span className="text-[#E6C200] font-bold">W </span>110.
                </div>
                <div>
                  <span className="font-semibold">-110:</span> For every <span className="text-[#E6C200] font-bold">W </span>110 bet, you can win <span className="text-[#E6C200] font-bold">W </span>100. 
                </div>
                <div>
                  <span className="font-semibold">Over/Under:</span> Over = slower than listed time; Under = faster than listed time.
                </div>
                <div className="border-t pt-3 mt-3">
                  <div className="font-semibold mb-2">Rules:</div>
                  <ul className="space-y-1 text-xs">
                    <li>• Wager Max: <span className="text-[#E6C200] font-bold">W </span>8</li>
                    <li>• Can't bet on (or against) yourself</li>
                    <li>• Can't bet what you don't have</li>
                    <li>• D<span className="text-cyan-400">AI</span>SY™ don't do decimals</li>
                    <li>• Cheaters/colluders expelled</li>
                    <li>• Tie goes to the runner</li>
                    <li>• Wagers subject to D<span className="text-cyan-400">AI</span>SY™ approval</li>
                    <li>• Wagers may be voided at any time for any reason</li>
                    <li>• The horse always wins 🐎🤖🪽8️⃣</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Legacy Brooklyn Mile cards removed */}

        {/* Bet Slip Display */}
        {showBetSlipPopup && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 px-4">
            <div className="w-[90vw] max-w-[360px] sm:max-w-[440px] bg-[rgba(30,30,30,0.92)] rounded-2xl text-white p-4 sm:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.18)]">
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
                <span style={{display: 'inline-block', padding: '0.25rem 0.75rem', background: 'rgba(255,255,255,0.13)', borderRadius: 4, color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem', letterSpacing: '0.1em', marginBottom: '1rem', fontWeight: 500, textTransform: 'uppercase', fontStyle: 'italic'}}>Wager</span>
                <div className="flex items-center gap-2">
                  <span style={{fontSize: '0.95rem', padding: '0.1rem 0.4rem', background: 'rgba(0,0,0,0.5)', borderRadius: 6}}>
                    D<span style={{color: '#00CED1'}}>AI</span>SY™
                  </span>
                </div>
              </div>
              <div style={{fontSize: '1.05rem', lineHeight: 1.6, marginBottom: '1.2rem'}}>
                <div style={{marginBottom: '0.7rem'}}><b>Wagerer:</b> {activeBet === 'generic' && genSlip ? genSlip.wagerer : activeBet === 'mare-affair' ? mareAffairUser : activeBet === 'patrick' ? patrickUser : activeBet === 'milk' ? milkUser : activeBet === 'melathon' ? melathonUser : activeBet === 'trimbawolf' ? trimbawolfUser : 'Guest'}</div>
                {activeBet !== 'generic' && (
                <div style={{marginBottom: '0.7rem'}}><b>Event:</b> Brooklyn M*le (8/3/25)</div>
                )}
                <div style={{marginBottom: '0.7rem'}}>
                  <b>Pick:</b>{' '}
                  {activeBet === 'generic' && genSlip
                    ? (genSlip.variant === 'closest'
                        ? `${genSlip.miner} closest to the PWP${genSlip.heatSize ? ` (Heat size: ${genSlip.heatSize})` : ''}`
                        : `${genSlip.miner} wins 3/19 day`)
                    : activeBet === 'mare-affair' ? (mareAffairBet === 'rachoon' ? 'Rachoon' : mareAffairBet === 'kat' ? 'KAT' : '')
                    : activeBet === 'patrick' ? (patrickBet === 'patrick-over' ? 'Other Patrick over 5:40.5' : 'Other Patrick under 5:40.5')
                    : activeBet === 'milk' ? (milkBet === 'milk-over' ? 'Milk over 5:22.5' : 'Milk under 5:22.5')
                    : activeBet === 'melathon' ? (melathonBet === 'melathon-over' ? 'Melathon over 6:15' : 'Melathon under 6:15')
                    : activeBet === 'trimbawolf' ? (trimbawolfBet === 'trimbawolf-over' ? 'Trimbawolf over 4:59.5' : 'Trimbawolf under 4:59.5')
                    : ''}
                </div>
                <div style={{marginBottom: '0.7rem'}}>
                  <b>Staked:</b> <span className="text-[#E6C200] font-bold">W</span> {activeBet === 'generic' && genSlip ? genSlip.amount : activeBet === 'mare-affair' ? mareAffairAmount : activeBet === 'patrick' ? patrickAmount : activeBet === 'milk' ? milkAmount : activeBet === 'melathon' ? melathonAmount : activeBet === 'trimbawolf' ? trimbawolfAmount : 0}
                </div>
              </div>
              <hr style={{borderColor: 'rgba(255,255,255,0.13)', margin: '1.2rem 0'}} />
              <div style={{fontSize: '0.95rem', marginBottom: '0.5rem'}}>
                {(activeBet === 'generic' && genSlip) ? genSlip.wagerer : activeBet === 'mare-affair' ? mareAffairUser : activeBet === 'patrick' ? patrickUser : activeBet === 'milk' ? milkUser : activeBet === 'melathon' ? melathonUser : activeBet === 'trimbawolf' ? trimbawolfUser : 'Guest'} collects <b>
                  <span className="text-[#E6C200] font-bold">W</span>{' '}
                  {`${(activeBet === 'generic' && genSlip ? (genSlip.variant === 'closest' && genSlip.heatSize ? genSlip.amount * genSlip.heatSize : genSlip.amount * 2) : (activeBet === 'mare-affair' ? mareAffairAmount : activeBet === 'patrick' ? patrickAmount : activeBet === 'milk' ? milkAmount : activeBet === 'melathon' ? melathonAmount : activeBet === 'trimbawolf' ? trimbawolfAmount : 0) * 2).toFixed(0)}`}
                </b> if {activeBet === 'generic' && genSlip
                  ? (genSlip.variant === 'closest'
                      ? `${genSlip.miner} finishes closest to PWP in ${genSlip.heatSize}-miner heat`
                      : `${genSlip.miner} wins 3/19 day`)
                  : activeBet === 'mare-affair'
                    ? (mareAffairBet === 'rachoon' ? 'Rachoon wins Mare Affair' : mareAffairBet === 'kat' ? 'KAT wins Mare Affair' : '')
                    : activeBet === 'patrick'
                      ? (patrickBet === 'patrick-over' ? 'Other Patrick runs over 5:40.5' : 'Other Patrick runs under 5:40.5')
                      : activeBet === 'milk'
                        ? (milkBet === 'milk-over' ? 'Milk runs over 5:22.5' : 'Milk runs under 5:22.5')
                        : activeBet === 'melathon'
                          ? (melathonBet === 'melathon-over' ? 'Melathon runs over 6:15' : 'Melathon runs under 6:15')
                          : activeBet === 'trimbawolf'
                            ? (trimbawolfBet === 'trimbawolf-over' ? 'Trimbawolf runs over 4:59.5' : 'Trimbawolf runs under 4:59.5')
                            : ''}.
              </div>
              
              <div className="text-center mt-4">
                <p className="text-sm text-gray-300">
                  Submit wager to D<span className="text-cyan-400">AI</span>SY™: <a href="sms:9299254744" className="text-wingo-600 hover:text-wingo-700 font-medium">929-WAK-GRIG</a>
                </p>
                <button
                  onClick={() => setShowBetSlipPopup(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-700 mt-3"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PWP Calculator Modal */}
        {showPwpCalc && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-[340px] sm:max-w-md bg-white rounded-2xl shadow-xl p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">What's my PentaWingoPoint (PWP)?</h3>
                <button onClick={() => setShowPwpCalc(false)} className="text-gray-500 hover:text-gray-700 text-xl" aria-label="Close">×</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div className="sm:col-span-1">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Preliminary PentaWingo (PPW)</label>
                  <input type="text" placeholder="mm:ss" value={pwpCalcTime} onChange={(e) => setPwpCalcTime(e.target.value)} className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-wingo-500 focus:border-wingo-500" style={{ fontSize: '16px' }} />
                </div>
                <div className="sm:col-span-1">
                  <select value={pwpCalcEffort} onChange={(e) => setPwpCalcEffort(e.target.value as any)} className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-wingo-500 focus:border-wingo-500" style={{ fontSize: '16px' }}>
                    <option value="">Select PPW effort level</option>
                    <option value="all_out">All Out</option>
                    <option value="pretty_hard">AL G. Pace (5k)</option>
                    <option value="hustle">Hustle (HM)</option>
                    <option value="super_chill">Chill (Pub Run)</option>
                  </select>
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Target PWP</label>
                  <div className="w-full px-2 py-2 text-sm border border-amber-300 rounded-md bg-amber-50 min-h-[38px] flex items-center justify-center font-semibold text-amber-700">{pwpCalcResult || <span className="text-gray-400">—</span>}</div>
                </div>
              </div>
              <p className="text-[10px] text-gray-500 mt-2 text-center">Multipliers — All Out: 1.25 • AL G. (5k): 1.15 • Hustle (HM): 1.10 • Pub: 0.85</p>
              <div className="text-center mt-3">
                <button onClick={() => setShowPwpCalc(false)} className="inline-block bg-wingo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-wingo-700">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* OBWI Index Modal */}
        {showObwiIndex && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-[340px] sm:max-w-md bg-white rounded-2xl shadow-xl p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">OBWI Index</h3>
                <button onClick={() => setShowObwiIndex(false)} className="text-gray-500 hover:text-gray-700 text-xl" aria-label="Close">×</button>
              </div>
              <p className="text-xs text-gray-600 mb-3">|Actual − PWP| ÷ PWP (in seconds). Lower is better.</p>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                <div className="sm:col-span-1">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Actual OBWI Time</label>
                  <input type="text" placeholder="mm:ss" value={obwiActual} onChange={(e) => setObwiActual(e.target.value)} className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-wingo-500 focus:border-wingo-500" style={{ fontSize: '16px' }} />
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">PentaWingoPoint (PWP)</label>
                  <input type="text" placeholder="mm:ss" value={obwiPwp} onChange={(e) => setObwiPwp(e.target.value)} className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-wingo-500 focus:border-wingo-500" style={{ fontSize: '16px' }} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">OBWI Index</label>
                  <div className="w-full px-2 py-2 text-sm border border-indigo-300 rounded-md bg-indigo-50 min-h-[38px] flex items-center justify-center font-semibold text-indigo-700">{obwiScore || <span className="text-gray-400">—</span>}</div>
                </div>
              </div>
              <ul className="list-disc pl-5 space-y-1 text-[12px] mt-3 text-gray-700">
                <li>Winner is closest (proportionally) to their PWP.</li>
                <li>Ex 1: 10:10 vs 10:00 ⇒ 10 / 600 = 0.0167</li>
                <li>Ex 2: 8:55 vs 8:15 ⇒ 40 / 495 = 0.0808</li>
              </ul>
              <div className="text-center mt-3">
                <button onClick={() => setShowObwiIndex(false)} className="inline-block bg-wingo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-wingo-700">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Decimal Warning Popup */}
        {showDecimalPopup && (
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="bg-white border-2 border-wingo-600 rounded-lg shadow-lg p-4 animate-fade-in-out">
              <div className="flex items-center">
                <span className="text-wingo-600 font-medium">DAISY™ don't do decimals</span>
              </div>
            </div>
          </div>
        )}

        {/* Max Wager Warning Popup */}
        {showMaxWagerPopup && (
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="bg-white border-2 border-wingo-600 rounded-lg shadow-lg p-4 animate-fade-in-out">
              <div className="flex items-center">
                <span className="text-wingo-600 font-medium">DAISY™ says 8 tops.</span>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
} 